import doNothing from '@opsbase/lib-general/utils/doNothing';
import i18n from '@opsbase/lib-i18n';
import Palette from '@opsbase/lib-palette';
import useSelf from '@opsbase/react-ctrl-self/useSelf';
import useTeamboardEmailInstructions from '@opsbase/react-gql-hooks/teamboard/useTeamboardEmailInstructions';
import useTeamboardFindById from '@opsbase/react-gql-hooks/teamboard/useTeamboardFindById';
import useTeamboardReset from '@opsbase/react-gql-hooks/teamboard/useTeamboardReset';
import useTeamboardUnlock from '@opsbase/react-gql-hooks/teamboard/useTeamboardUnlock';
import useTeamboardUpdate from '@opsbase/react-gql-hooks/teamboard/useTeamboardUpdate';
import useCss from '@opsbase/web-css';
import PlaceChooser from '@opsbase/web-data-choosers/PlaceChooser';
import TeamChooser from '@opsbase/web-data-choosers/TeamChooser';
import Dialog from '@opsbase/web-dialogs/Dialog';
import DialogSize from '@opsbase/web-dialogs/Dialog/DialogSize';
import Card from '@opsbase/web-layout/Card';
import Horizontal from '@opsbase/web-layout/Horizontal';
import Pane from '@opsbase/web-layout/Pane';
import Vertical from '@opsbase/web-layout/Vertical';
import useParams from '@opsbase/web-nav/useParams';
import useNotificationActions from '@opsbase/web-notification/useNotificationActions';
import LanguagePicker from '@opsbase/web-pickers/LanguagePicker';
import Button from '@opsbase/web-ui/Button';
import Switch from '@opsbase/web-ui/Switch';
import Text from '@opsbase/web-ui/Text';
import pick from 'lodash/pick';
import React, { useCallback, useMemo } from 'react';
const makeMyClasses = () => ({
    root: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '10px 160px',
    },
    code: {
        fontSize: 36,
        lineHeight: '40px',
        backgroundColor: Palette.BackgroundGrey.main,
        border: '1px solid ' + Palette.InputBorderGrey.main,
        margin: 16,
        padding: 16,
        borderRadius: 4,
    },
    lastaccessed: {
        fontSize: 32,
        lineHeight: '34px',
        margin: 32,
    },
});
export function TeamboardEditDialog() {
    const { id } = useParams();
    const self = useSelf();
    const { data: teamboard } = useTeamboardFindById({ variables: { id } });
    const updateTeamboard = useTeamboardUpdate();
    const resetTeamboard = useTeamboardReset();
    const emailTeamboardInstructions = useTeamboardEmailInstructions();
    const unlockTeamboard = useTeamboardUnlock();
    const { raise } = useNotificationActions();
    const { place, teams, allTeams } = teamboard || {};
    const myCss = useCss(makeMyClasses);
    const doUpdateTeamboard = useMemo(() => async (patch) => updateTeamboard({
        id: teamboard.id,
        patch,
    }, {
        optimisticResponse: {
            __typename: 'Mutation',
            updateTeamboard: {
                __typename: 'Teamboard',
                ...teamboard,
                patch,
            },
        },
    }), [teamboard, updateTeamboard]);
    const handleChangeTitle = useCallback(async (title) => {
        if (!title)
            return;
        doUpdateTeamboard({
            title,
        });
    }, [doUpdateTeamboard]);
    const handleChangePlace = useCallback(async (changedPlace) => {
        if (!changedPlace || !changedPlace.length)
            return;
        return doUpdateTeamboard({
            place: pick(changedPlace[0], 'id'),
            teams: [],
        });
    }, [doUpdateTeamboard]);
    const handleChangeShowStaffIssues = useCallback(async () => {
        return doUpdateTeamboard({
            showStaffIssues: !teamboard.showStaffIssues,
        });
    }, [doUpdateTeamboard, teamboard]);
    const handleChangeSelfieRequired = useCallback(async () => {
        return doUpdateTeamboard({
            selfieRequired: !teamboard.selfieRequired,
        });
    }, [doUpdateTeamboard, teamboard]);
    const handleChangeTeam = useCallback(async (changedTeams) => doUpdateTeamboard({
        teams: changedTeams.map(t => pick(t, 'id')),
        allTeams: changedTeams.length ? false : allTeams,
    }), [allTeams, doUpdateTeamboard]);
    const handleSelectAllTeams = useCallback(async (flag) => {
        if (flag) {
            return doUpdateTeamboard({
                allTeams: true,
                teams: [],
            });
        }
        else {
            return doUpdateTeamboard({
                allTeams: false,
            });
        }
    }, [doUpdateTeamboard]);
    const handleChangeLanguage = useCallback(language => {
        doUpdateTeamboard({
            preferences: { ...teamboard.preferences, language },
        }).catch(doNothing);
    }, [doUpdateTeamboard, teamboard]);
    const handleEmailInstructions = useCallback(async () => {
        await raise(emailTeamboardInstructions({ id }), i18n.t('instructions-emailed-successfully'));
    }, [emailTeamboardInstructions, id, raise]);
    const handleReset = useCallback(async () => {
        await raise(resetTeamboard({ id }), i18n.t('teamboard-reset-successfully'));
    }, [id, raise, resetTeamboard]);
    const handleUnlock = useCallback(async () => {
        await unlockTeamboard({ id });
    }, [unlockTeamboard, id]);
    const buttonsRight = useMemo(() => {
        if (!teamboard)
            return null;
        if (teamboard.locked)
            return (React.createElement(Button, { color: Palette.BadRed, onClick: handleUnlock, label: i18n.t('unlock-user') }));
        return (React.createElement(Horizontal, { gutter: true },
            React.createElement(Button, { onClick: handleReset, label: i18n.t('reset-the-teamboard') }),
            React.createElement(Button, { onClick: handleEmailInstructions, label: i18n.t('email-these-instructions') })));
    }, [teamboard, handleUnlock, handleReset, handleEmailInstructions]);
    return (React.createElement(Dialog, { size: DialogSize.X600_Y550, editableTitle: true, title: teamboard ? teamboard.title : i18n.t('loading'), onChangeTitle: handleChangeTitle, buttonsRight: buttonsRight }, !!teamboard && (React.createElement(Vertical, { gutter: true },
        React.createElement(Pane, null,
            React.createElement(Text, null, i18n.t('teamboard-place')),
            React.createElement(PlaceChooser, { required: true, value: [place], onChange: handleChangePlace })),
        React.createElement(Pane, null,
            React.createElement(Text, null, i18n.t('select-teams')),
            React.createElement(TeamChooser, { multi: true, required: true, value: teams, onChange: handleChangeTeam, allSelectable: true, allSelected: allTeams, onSelectAll: handleSelectAllTeams, canCreate: true })),
        React.createElement(Pane, null,
            React.createElement(Text, null, i18n.t('prefered-language')),
            React.createElement(LanguagePicker, { white: true, noMargin: true, value: teamboard === null || teamboard === void 0 ? void 0 : teamboard.preferences.language, allowed: self.settings.languages, onChange: handleChangeLanguage })),
        React.createElement(Card, { style: {
                borderColor: Palette.InputBorderGrey.main,
                borderRadius: 4,
            } },
            React.createElement(Vertical, { shrink: true, gutter: true, padding: true },
                React.createElement(Pane, { center: true },
                    React.createElement(Text, null, i18n.t('setup-instructions'))),
                React.createElement(Pane, { center: true },
                    React.createElement(Horizontal, { shrink: true, centered: true },
                        React.createElement(Pane, null,
                            React.createElement(Text, { className: myCss.code }, teamboard.securityBits[0])),
                        React.createElement(Pane, null,
                            React.createElement(Text, { className: myCss.code }, teamboard.securityBits[1])),
                        React.createElement(Pane, null,
                            React.createElement(Text, { className: myCss.code }, teamboard.securityBits[2])))))),
        React.createElement(Card, null,
            React.createElement(Vertical, null,
                React.createElement(Horizontal, { padding: true, gutter: true, justify: "flex-start" },
                    React.createElement(Switch, { small: true, onChange: handleChangeSelfieRequired, checked: teamboard === null || teamboard === void 0 ? void 0 : teamboard.selfieRequired }),
                    React.createElement(Text, null, i18n.t('selfie-required'))),
                React.createElement(Horizontal, { padding: true, gutter: true, justify: "flex-start" },
                    React.createElement(Switch, { small: true, onChange: handleChangeShowStaffIssues, checked: teamboard === null || teamboard === void 0 ? void 0 : teamboard.showStaffIssues }),
                    React.createElement(Text, null, i18n.t('show-staff-issues'))))),
        teamboard.locked && (React.createElement(Pane, null,
            React.createElement(Text, { color: Palette.BadRed }, i18n.t('account-locked'))))))));
}
export default TeamboardEditDialog;
